import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addCharacter,
  can,
  deleteCharacter,
  extractCharacters,
  fillCharacter,
  findLines,
  getCharacters,
  mergeCharacter,
  queueLivePortraits,
  queuePortraits,
  updateCharacter,
  setNarrator,
  setSpeaksAlone,
  type Character,
  type CharacterEdit,
  type Characters,
} from "../api";
import { Amount } from "../components/Amount";
import ConfirmDelete from "../components/ConfirmDelete";
import MutationError from "../components/MutationError";
import SheetHeader from "../components/SheetHeader";
import { useT } from "../i18n";
import { primaryOrg, useSession } from "../session";
import DialogueSection from "../studio/DialogueSection";
import PortraitButton from "../studio/PortraitButton";
import CharacterForms from "../studio/CharacterForms";
import { formatMicros } from "../studio/money";
import { narratorPress, oneSpeakerPress, type NarratorPress } from "../studio/narrator";

/**
 * CharactersPanel is the studio's cast list (ADR-0034).
 *
 * One button reads every chapter and compiles the roster; the cost is beside
 * it before the tap, the way the narration estimate is, and the run's real
 * cost is on the result. The list is then the author's: rename, describe,
 * add, delete, merge — and a later run keeps their words.
 */
export default function CharactersPanel({
  bookID,
  bare = false,
  locked = false,
}: {
  bookID: string;
  bare?: boolean;
  /**
   * The cast is derived and may not be hand-edited (ADR-0093). The list, the
   * faces and the descriptions still show — a linked book's author has every
   * reason to look at its cast and none to be shown a blank screen instead.
   * Only the controls that would write go.
   */
  locked?: boolean;
}) {
  const t = useT();
  const client = useQueryClient();
  const { me } = useSession();
  const org = primaryOrg(me);
  const [editing, setEditing] = useState<Character | "new" | null>(null);
  // Choosing whose faces to draw at once (ADR-0049), or whose portraits to
  // bring to life (ADR-0127): null when not choosing. One bar, two modes.
  const [picking, setPicking] = useState<Set<string> | null>(null);
  const [mode, setMode] = useState<"draw" | "live">("draw");
  const [queued, setQueued] = useState<{ mode: "draw" | "live"; count: number } | null>(null);

  const characters = useQuery({
    queryKey: ["characters", bookID, "draft"],
    queryFn: () => getCharacters(bookID),
    retry: false,
    // Polled while the cast is being read, or while any face is being drawn.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.extraction?.status === "running") return 1500;
      if (data?.dialogue?.run?.status === "running") return 3000;
      return data?.characters.some(
        (c) =>
          c.portrait_batch?.status === "pending" || c.live_portrait_batch?.status === "pending",
      )
        ? 3000
        : false;
    },
  });

  const settle = (data: Characters) => {
    client.setQueryData(["characters", bookID, "draft"], data);
    // The spend moved.
    void client.invalidateQueries({ queryKey: ["usage"] });
  };
  const extract = useMutation({
    mutationFn: () => extractCharacters(bookID),
    onSuccess: settle,
  });
  const save = useMutation({
    mutationFn: (v: { id?: string; edit: CharacterEdit }) =>
      v.id ? updateCharacter(bookID, v.id, v.edit) : addCharacter(bookID, v.edit),
    onSuccess: (data) => {
      settle(data);
      setEditing(null);
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteCharacter(bookID, id),
    onSuccess: (data) => {
      settle(data);
      setEditing(null);
    },
  });
  const batch = useMutation({
    mutationFn: (v: { mode: "draw" | "live"; ids: string[] }) =>
      v.mode === "live" ? queueLivePortraits(bookID, v.ids) : queuePortraits(bookID, v.ids),
    onSuccess: (data, v) => {
      settle(data);
      setPicking(null);
      setQueued({ mode: v.mode, count: v.ids.length });
    },
  });
  // A character added since its chapters were attributed is offered the
  // lines the model gave to someone else there (ADR-0123 §5).
  const lines = useMutation({
    mutationFn: (id: string) => findLines(bookID, id),
    onSuccess: settle,
  });
  const narrator = useMutation({
    mutationFn: (p: NarratorPress) =>
      "speaksAlone" in p
        ? setSpeaksAlone(bookID, p.id, p.speaksAlone)
        : setNarrator(bookID, p.id, p.narrates),
    onSuccess: settle,
  });
  const merge = useMutation({
    mutationFn: (v: { id: string; into: string }) => mergeCharacter(bookID, v.id, v.into),
    onSuccess: (data) => {
      settle(data);
      setEditing(null);
    },
  });

  const c = characters.data;
  if (characters.isError) {
    return (
      <section
        className={bare ? "" : "mt-10 border-t border-line pt-5"}
        data-testid="characters-panel"
      >
        {!bare && <h2 className="text-sm font-medium">{t.characters.heading}</h2>}
        <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">
          {t.characters.loadFailed}
        </p>
      </section>
    );
  }
  if (!c) return null;

  // Every control that spends or writes reads this, so one flag closes them all.
  const canDraw = can(org, "book.augment") && !locked;
  // Labels are not roster writes, so a linked book's lines stay the author's
  // to check and update (ADR-0123).
  const canSpend = can(org, "book.augment");
  const dialogue = c.dialogue;
  const pending = (ch: Character) => ch.portrait_batch?.status === "pending";
  const livePending = (ch: Character) => ch.live_portrait_batch?.status === "pending";
  // Ticked to start with: those with no face that nothing is drawing yet.
  const withoutFaces = c.characters.filter((ch) => !ch.portrait && !pending(ch)).map((ch) => ch.id);
  const choosable = c.characters.filter((ch) => !pending(ch)).map((ch) => ch.id);
  // A live portrait is made from the portrait: only those with one, and not
  // while one is being made. Ticked to start with: those that have none yet.
  const liveChoosable = c.characters
    .filter((ch) => ch.portrait && !livePending(ch) && !pending(ch))
    .map((ch) => ch.id);
  const withoutLive = c.characters
    .filter((ch) => ch.portrait && !ch.live_portrait && !livePending(ch) && !pending(ch))
    .map((ch) => ch.id);
  const disabledFor = (ch: Character) =>
    mode === "live" ? !liveChoosable.includes(ch.id) : pending(ch);
  const each = mode === "live" ? c.live_portrait_micros : c.batch_portrait_micros;
  const words = mode === "live" ? t.live : null;
  const run = c.extraction;
  const running = run?.status === "running";
  const busy = extract.isPending || running;
  const never = !run;

  return (
    <section
      className={bare ? "" : "mt-10 border-t border-line pt-5"}
      data-testid="characters-panel"
      data-status={run?.status ?? "none"}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          {!bare && <h2 className="text-sm font-medium">{t.characters.heading}</h2>}
          <p className="mt-1 max-w-prose text-xs leading-5 text-muted">{t.characters.note}</p>
        </div>
        {c.chapter_total > 0 && !locked && (
          <button
            type="button"
            data-testid="extract-characters"
            disabled={busy}
            onClick={() => extract.mutate()}
            className={
              never
                ? "shrink-0 rounded-control bg-sage px-3 py-1.5 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-40"
                : "shrink-0 rounded-control border border-line px-3 py-1.5 text-sm transition-colors hover:bg-sunk disabled:opacity-40"
            }
          >
            {running
              ? t.characters.extracting
              : never
                ? t.characters.extract
                : t.characters.extractAgain}
          </button>
        )}
      </div>

      {/* What the tap costs, or what the run did. One line, before the list. */}
      <div className="mt-3 space-y-1 text-xs text-muted">
        {c.chapter_total === 0 && !run && (
          <p data-testid="characters-no-text">{t.characters.noText}</p>
        )}
        {running && run && (
          <p data-testid="characters-progress">
            {run.chapter_done + run.chapter_failed < run.chapter_total
              ? t.characters.progress(
                  Math.min(run.chapter_done + run.chapter_failed + 1, run.chapter_total),
                  run.chapter_total,
                )
              : t.characters.merging}
          </p>
        )}
        {!running && run && run.status !== "failed" && (
          <p data-testid="characters-result" className="text-ink-soft">
            <Amount
              sentence={(a) => t.characters.result(run.characters, run.chapter_done, a)}
              amount={run.cost_display}
            />
          </p>
        )}
        {!running && run?.status === "partial" && (
          <p
            data-testid="characters-partial"
            className="rounded-control bg-amber-50 px-3 py-2 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
          >
            {t.characters.partial(run.chapter_failed)}
            {run.error && <span dir="auto"> {run.error}</span>}
          </p>
        )}
        {!running && run?.status === "failed" && (
          <p
            role="alert"
            data-testid="characters-failed"
            className="text-red-600 dark:text-red-400"
          >
            {t.characters.failed}
            {run.error && <span dir="auto"> {run.error}</span>}
          </p>
        )}
        {!running && c.chapter_total > 0 && (never || c.stale > 0) && (
          <p data-testid="characters-estimate">
            <Amount
              sentence={(a) =>
                never ? t.characters.estimate(c.chapter_total, a) : t.characters.stale(c.stale, a)
              }
              amount={c.estimate_display}
            />
          </p>
        )}
        {/* How far the cast is from being able to supply references
            (ADR-0039). Said here because this is the screen where somebody
            fills the faces in, and "how many are left" is the only thing they
            want to know while doing it. */}
        {c.characters.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <p data-testid="cast-progress">
              {t.portraits.castProgress(
                c.characters.filter((ch) => ch.portrait).length,
                c.characters.length,
              )}
            </p>
            {canDraw && !picking && (
              <button
                type="button"
                data-testid="batch-start"
                onClick={() => {
                  batch.reset();
                  setMode("draw");
                  setPicking(new Set(withoutFaces));
                }}
                className="rounded-full border border-sage px-2.5 py-0.5 text-xs font-medium text-sage-deep transition-colors hover:bg-sage-soft"
              >
                {t.portraits.batchStart}
              </button>
            )}
            {/* Only where this server can make them (ADR-0127), and only once
                somebody has a face to bring to life. */}
            {canDraw && !picking && c.live_portraits_available && liveChoosable.length > 0 && (
              <button
                type="button"
                data-testid="live-start"
                onClick={() => {
                  batch.reset();
                  setMode("live");
                  setPicking(new Set(withoutLive));
                }}
                className="rounded-full border border-sage px-2.5 py-0.5 text-xs font-medium text-sage-deep transition-colors hover:bg-sage-soft"
              >
                {t.live.start}
              </button>
            )}
          </div>
        )}
        {queued?.mode === "draw" && c.characters.some(pending) && (
          <p data-testid="batch-queued" role="status">
            {t.portraits.batchQueued(queued.count)}
          </p>
        )}
        {queued?.mode === "live" && c.characters.some(livePending) && (
          <p data-testid="live-queued" role="status">
            {t.live.queued(queued.count)}
          </p>
        )}
        {extract.isError && (
          <p role="alert" className="text-red-600 dark:text-red-400">
            {extract.error instanceof Error ? extract.error.message : t.characters.extractFailed}
          </p>
        )}
      </div>

      <DialogueSection
        bookID={bookID}
        roster={c}
        canSpend={canSpend}
        onRoster={(next) => client.setQueryData(["characters", bookID, "draft"], next)}
      />

      {c.characters.length > 0 && (
        <NarratorChips
          cast={c.characters}
          disabled={!canDraw || narrator.isPending}
          error={narrator.error}
          onPress={(p) => narrator.mutate(p)}
        />
      )}

      {c.characters.length === 0 ? (
        run && !running ? (
          <p className="mt-3 text-sm text-muted" data-testid="characters-empty">
            {t.characters.empty}
          </p>
        ) : null
      ) : (
        <ul
          data-testid="character-list"
          className="mt-3 divide-y divide-line rounded-card border border-line bg-surface"
        >
          {c.characters.map((ch) => (
            <li key={ch.id} data-testid="character" className="px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                {picking && (
                  <input
                    type="checkbox"
                    data-testid="batch-pick"
                    aria-label={words ? words.choose(ch.name) : t.portraits.batchChoose(ch.name)}
                    disabled={disabledFor(ch) || batch.isPending}
                    checked={picking.has(ch.id)}
                    onChange={(e) => {
                      const next = new Set(picking);
                      if (e.target.checked) next.add(ch.id);
                      else next.delete(ch.id);
                      setPicking(next);
                    }}
                    className="mt-4 size-5 shrink-0 accent-sage"
                  />
                )}
                {/* The face, which is what every picture of a scene this
                    character is in gets drawn from (ADR-0039). It leads the
                    row because a book cannot be illustrated until every one of
                    them is filled. */}
                <PortraitButton
                  bookID={bookID}
                  character={ch}
                  canDescribe={canDraw}
                  prices={c.draw_prices}
                  onChanged={(next) => client.setQueryData(["characters", bookID, "draft"], next)}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">
                    <span dir="auto">{ch.name}</span>
                    {ch.role && (
                      <span className="font-normal text-muted" dir="auto">
                        {" "}
                        · {ch.role}
                      </span>
                    )}
                    {/* A live portrait exists (ADR-0127): the face opens it. */}
                    {ch.live_portrait && (
                      <span
                        data-testid="live-badge"
                        aria-label={t.live.badgeLabel(ch.name)}
                        title={t.live.badgeLabel(ch.name)}
                        className="ms-2 inline-flex items-center gap-1 rounded-full bg-sage-soft px-2 py-0.5 align-middle text-[0.65rem] font-medium text-sage-deep"
                      >
                        <span
                          className="size-1.5 animate-pulse rounded-full bg-sage"
                          aria-hidden="true"
                        />
                        {t.live.badge}
                      </span>
                    )}
                  </p>
                  {livePending(ch) && (
                    <p
                      data-testid="live-making"
                      aria-live="polite"
                      className="mt-0.5 text-xs text-sage-deep"
                    >
                      <span className="animate-pulse">{t.live.making}</span>
                    </p>
                  )}
                  {ch.live_portrait_batch?.status === "failed" && (
                    <p
                      role="alert"
                      data-testid="live-failed"
                      data-reason={ch.live_portrait_batch.reason}
                      className="mt-0.5 text-xs text-red-600 dark:text-red-400"
                    >
                      {(ch.live_portrait_batch.reason &&
                        t.live.reasons[ch.live_portrait_batch.reason]) ||
                        t.live.failedFallback}
                    </p>
                  )}
                  {/* Said beside the name, not under the face: the reason
                      is a sentence, and the face column is a thumbnail wide. */}
                  {ch.portrait_batch?.status === "failed" && (
                    <p
                      role="alert"
                      data-testid="portrait-batch-failed"
                      data-reason={ch.portrait_batch.reason}
                      className="mt-0.5 text-xs text-red-600 dark:text-red-400"
                    >
                      {(ch.portrait_batch.reason &&
                        t.portraits.batchReasons[ch.portrait_batch.reason]) ||
                        t.portraits.batchFailedFallback}
                    </p>
                  )}
                  {ch.aliases.length > 0 && (
                    <p className="text-xs text-muted" dir="auto">
                      {t.characters.alsoKnownAs(ch.aliases.join("، "))}
                    </p>
                  )}
                </div>
                {!locked && (
                  <button
                    type="button"
                    data-testid="edit-character"
                    onClick={() => setEditing(ch)}
                    className="shrink-0 rounded-control border border-line px-2.5 py-1 text-xs transition-colors hover:bg-sunk"
                  >
                    {t.characters.edit}
                  </button>
                )}
              </div>
              {/* What readers see first, labelled, because the description
                  under it is the part they only see with spoilers
                  (requirement 0011). */}
              {ch.introduction && (
                <p className="mt-1 text-sm leading-6" data-testid="character-introduction">
                  <span className="me-1 text-xs text-muted">{t.characters.readersSee}:</span>
                  <span dir="auto">{ch.introduction}</span>
                </p>
              )}
              {ch.description && (
                <p className="mt-1 text-sm leading-6 text-ink-soft" dir="auto">
                  {ch.description}
                </p>
              )}
              <p className="mt-1 text-xs text-muted" dir="auto">
                {ch.appearances.length > 0 &&
                  t.characters.inChapters(ch.appearances.map((a) => a.title).join("، "))}
                {(ch.edited || ch.source === "author") && (
                  <span className="ms-2 rounded-full bg-sunk px-2 py-0.5">
                    {ch.source === "author" ? t.characters.byAuthor : t.characters.edited}
                  </span>
                )}
                {dialogue.attributed > 0 && (dialogue.lines[ch.id] ?? 0) > 0 && (
                  <span className="ms-2" data-testid="character-lines">
                    {t.dialogue.lines(dialogue.lines[ch.id] ?? 0)}
                  </span>
                )}
              </p>
              {/* Their other shapes, each with its own face (ADR-0159). */}
              <CharacterForms
                bookID={bookID}
                character={ch}
                canEdit={!locked}
                canDescribe={canDraw}
                prices={c.draw_prices}
                onChanged={(next) => client.setQueryData(["characters", bookID, "draft"], next)}
              />
              {canSpend &&
                dialogue.available &&
                dialogue.attributed > 0 &&
                !((dialogue.lines[ch.id] ?? 0) > 0) &&
                dialogue.run?.status !== "running" && (
                  <button
                    type="button"
                    data-testid="find-lines"
                    disabled={lines.isPending}
                    onClick={() => lines.mutate(ch.id)}
                    className="mt-1.5 rounded-full border border-line px-2.5 py-0.5 text-xs text-muted transition-colors hover:text-ink disabled:opacity-40"
                  >
                    {lines.isPending && lines.variables === ch.id
                      ? t.dialogue.findingLines
                      : t.dialogue.findLines}
                  </button>
                )}
            </li>
          ))}
        </ul>
      )}

      {picking && (
        /* Pinned to the bottom so the button stays under the thumb while the
           list scrolls; a phone's roster is longer than its screen. */
        <div
          data-testid="batch-bar"
          className="sticky bottom-0 z-10 -mx-4 mt-3 border-t border-line bg-surface px-4 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
        >
          <p className="text-xs leading-5 text-muted">
            {words ? words.pick : t.portraits.batchPick}
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
            {(words
              ? ([
                  ["batch-all", words.all, liveChoosable],
                  ["batch-faceless", words.without, withoutLive],
                  ["batch-none", words.none, []],
                ] as const)
              : ([
                  ["batch-all", t.portraits.batchAll, choosable],
                  ["batch-faceless", t.portraits.batchWithoutFaces, withoutFaces],
                  ["batch-none", t.portraits.batchNone, []],
                ] as const)
            ).map(([testid, label, ids]) => (
              <button
                key={testid}
                type="button"
                data-testid={testid}
                disabled={batch.isPending}
                onClick={() => setPicking(new Set(ids))}
                className="rounded-full border border-line px-2.5 py-1 text-muted transition-colors hover:text-ink disabled:opacity-40"
              >
                {label}
              </button>
            ))}
          </div>
          <MutationError
            className="mt-2"
            testId="batch-error"
            error={batch.error}
            fallback={words ? words.queueFailed : t.portraits.batchQueueFailed}
          />
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              data-testid="batch-cancel"
              disabled={batch.isPending}
              onClick={() => setPicking(null)}
              className="rounded-control border border-line px-4 py-2.5 text-sm transition-colors hover:bg-sunk disabled:opacity-40"
            >
              {words ? words.cancel : t.portraits.batchCancel}
            </button>
            <button
              type="button"
              data-testid="batch-draw"
              disabled={picking.size === 0 || batch.isPending}
              onClick={() => batch.mutate({ mode, ids: [...picking] })}
              className="flex-1 rounded-control bg-sage px-4 py-2.5 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-40"
            >
              {words ? words.make(picking.size) : t.portraits.batchDraw(picking.size)}
              {/* The price before the tap, worked out from the server's price
                  for one (ADR-0049). Only the amount is isolated left to
                  right: the separator belongs to the interface's direction,
                  and "·" reads as a zero in Persian. */}
              {picking.size > 0 && (
                <span data-testid="batch-price" className="font-normal opacity-80">
                  {t.book.sep}
                  <bdi dir="ltr">{formatMicros(picking.size * each)}</bdi>
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      {!locked && (
        <button
          type="button"
          data-testid="add-character"
          onClick={() => setEditing("new")}
          className="mt-3 rounded-control border border-line px-3 py-1.5 text-sm transition-colors hover:bg-sunk"
        >
          {t.characters.add}
        </button>
      )}
      {locked && <p className="mt-3 text-xs text-muted">{t.translation.lockedNote}</p>}

      {editing && (
        <CharacterSheet
          bookID={bookID}
          canFill={canDraw}
          character={editing === "new" ? null : editing}
          others={c.characters.filter((o) => editing === "new" || o.id !== editing.id)}
          pending={save.isPending || remove.isPending || merge.isPending}
          error={
            save.isError || remove.isError || merge.isError
              ? (save.error ?? remove.error ?? merge.error) instanceof Error
                ? ((save.error ?? remove.error ?? merge.error) as Error).message
                : t.characters.saveFailed
              : null
          }
          onSave={(edit) => save.mutate({ id: editing === "new" ? undefined : editing.id, edit })}
          onDelete={editing === "new" ? undefined : () => remove.mutate(editing.id)}
          onMerge={editing === "new" ? undefined : (into) => merge.mutate({ id: editing.id, into })}
          onClose={() => {
            setEditing(null);
            save.reset();
            remove.reset();
            merge.reset();
          }}
        />
      )}
    </section>
  );
}

/** splitAliases reads a comma-separated field, Persian or Latin commas. */
/**
 * NarratorChips picks the book's narrator (requirement 0071, ADR-0137): pill
 * chips, one per character and one for nobody, never a menu (a native select
 * covers a phone's screen). The narrator is the only one pressed; pressing
 * "nobody" clears whoever it is.
 *
 * Under them, whether the narrator is the book's only speaker (requirement
 * 0113, ADR-0180), as two chips like a book's story: the chips above name who
 * that is, and choosing another narrator moves it (narratorPress).
 */
function NarratorChips({
  cast,
  disabled,
  error,
  onPress,
}: {
  cast: Character[];
  disabled: boolean;
  error: unknown;
  onPress: (p: NarratorPress) => void;
}) {
  const t = useT();
  const current = cast.find((ch) => ch.narrates);
  const alone = current?.speaks_alone ?? false;
  const press = (p: NarratorPress | null) => p && onPress(p);
  const chip = (pressed: boolean) =>
    `rounded-full border px-3 py-1 text-xs transition-colors disabled:opacity-50 ${
      pressed ? "border-sage bg-sage-soft font-medium text-sage-deep" : "border-line hover:bg-sunk"
    }`;
  return (
    <div className="mt-4" data-testid="narrator">
      <h3 className="text-xs font-medium">{t.characters.narratorHeading}</h3>
      <p className="mt-0.5 max-w-prose text-xs leading-5 text-muted">{t.characters.narratorNote}</p>
      <div
        className="mt-2 flex flex-wrap gap-2"
        role="group"
        aria-label={t.characters.narratorHeading}
      >
        <button
          type="button"
          data-testid="narrator-none"
          aria-pressed={!current}
          disabled={disabled}
          onClick={() => press(narratorPress(cast, null))}
          className={chip(!current)}
        >
          {t.characters.narratorNone}
        </button>
        {cast.map((ch) => (
          <button
            key={ch.id}
            type="button"
            dir="auto"
            data-testid={`narrator-${ch.id}`}
            aria-pressed={ch.narrates}
            disabled={disabled}
            onClick={() => press(narratorPress(cast, ch.id))}
            className={chip(ch.narrates)}
          >
            {ch.name}
          </button>
        ))}
      </div>
      <h3 className="mt-4 text-xs font-medium">{t.characters.speakersHeading}</h3>
      <p className="mt-0.5 max-w-prose text-xs leading-5 text-muted" data-testid="one-speaker-note">
        {current ? t.characters.oneSpeakerNote : t.characters.oneSpeakerNeedsNarrator}
      </p>
      <div
        className="mt-2 flex flex-wrap gap-2"
        role="group"
        aria-label={t.characters.speakersHeading}
        data-testid="one-speaker"
        data-on={alone}
      >
        {[
          { on: false, label: t.characters.manySpeak, testID: "one-speaker-off" },
          { on: true, label: t.characters.oneSpeaker, testID: "one-speaker-on" },
        ].map((choice) => (
          <button
            key={String(choice.on)}
            type="button"
            data-testid={choice.testID}
            aria-pressed={alone === choice.on}
            disabled={disabled || !current}
            onClick={() => alone !== choice.on && press(oneSpeakerPress(cast))}
            className={chip(alone === choice.on)}
          >
            {choice.label}
          </button>
        ))}
      </div>
      <MutationError
        testId="narrator-error"
        error={error}
        fallback={t.characters.oneSpeakerFailed}
      />
    </div>
  );
}

export function splitAliases(raw: string): string[] {
  return raw
    .split(/[,،]/)
    .map((a) => a.trim())
    .filter((a) => a.length > 0);
}

/**
 * CharacterSheet edits one character, or adds one. Saving marks it as the
 * author's, so the next extraction keeps its words.
 */
function CharacterSheet({
  bookID,
  canFill,
  character,
  others,
  pending,
  error,
  onSave,
  onDelete,
  onMerge,
  onClose,
}: {
  bookID: string;
  /** Whether this account may spend on the book (`book.augment`). */
  canFill: boolean;
  character: Character | null;
  others: Character[];
  pending: boolean;
  error: string | null;
  onSave: (edit: CharacterEdit) => void;
  onDelete?: () => void;
  onMerge?: (into: string) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [name, setName] = useState(character?.name ?? "");
  const [aliases, setAliases] = useState(character?.aliases.join("، ") ?? "");
  const [role, setRole] = useState(character?.role ?? "");
  const [description, setDescription] = useState(character?.description ?? "");
  const [introduction, setIntroduction] = useState(character?.introduction ?? "");
  const [into, setInto] = useState("");
  const client = useQueryClient();
  // A suggestion, never a save (ADR-0054): it fills what is empty and adds
  // other names, and what the author typed stays.
  const fill = useMutation({
    mutationFn: () => fillCharacter(bookID, { name, aliases: splitAliases(aliases) }),
    onSuccess: (got) => {
      const known = splitAliases(aliases);
      const more = got.aliases.filter((a) => !known.includes(a));
      if (more.length > 0) setAliases([...known, ...more].join("، "));
      setRole((v) => (v.trim() ? v : got.role));
      setIntroduction((v) => (v.trim() ? v : got.introduction));
      setDescription((v) => (v.trim() ? v : got.description));
      void client.invalidateQueries({ queryKey: ["usage"] });
    },
  });
  const title = character ? t.characters.edit : t.characters.add;
  const field = "w-full rounded-control border border-line bg-surface px-3 py-2.5 text-base";

  return (
    <div
      role="dialog"
      aria-label={title}
      data-testid="character-sheet"
      className="fixed inset-x-0 bottom-0 z-40 max-h-[90vh] overflow-y-auto rounded-t-card border-t border-line bg-surface shadow-[0_-8px_24px_-12px_rgb(0_0_0/0.25)]"
    >
      <div className="mx-auto w-full max-w-3xl">
        <SheetHeader title={title} onClose={onClose} closeTestID="character-sheet-close" />
        <form
          className="space-y-3 px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]"
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim() || pending) return;
            onSave({ name, aliases: splitAliases(aliases), role, description, introduction });
          }}
        >
          {/* The face, so editing somebody shows who they are. Changing it is
              the portrait button's job, on the row. */}
          {character?.portrait && (
            <img
              data-testid="character-sheet-portrait"
              src={character.portrait.url}
              alt=""
              width={character.portrait.width}
              height={character.portrait.height}
              className="size-20 rounded-card border border-line object-cover"
            />
          )}
          <input
            dir="auto"
            autoFocus={!character}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t.characters.name}
            aria-label={t.characters.name}
            data-testid="character-name"
            className={field}
          />
          {canFill && (
            <div data-testid="character-fill-row" className="space-y-1">
              <button
                type="button"
                data-testid="character-fill"
                disabled={!name.trim() || fill.isPending || pending}
                onClick={() => fill.mutate()}
                className="rounded-full border border-sage px-3 py-1.5 text-sm font-medium text-sage-deep transition-colors hover:bg-sage-soft disabled:opacity-40"
              >
                {fill.isPending ? t.characters.filling : t.characters.fill}
              </button>
              {fill.data ? (
                <p data-testid="character-filled" className="text-xs text-muted">
                  {t.characters.filled(fill.data.passages)}{" "}
                  <bdi dir="ltr">{fill.data.cost_display}</bdi>
                </p>
              ) : (
                <p className="text-xs text-muted">{t.characters.fillHint}</p>
              )}
              <MutationError
                testId="character-fill-error"
                error={fill.error}
                fallback={t.characters.fillFailed}
              />
            </div>
          )}
          <input
            dir="auto"
            value={aliases}
            onChange={(e) => setAliases(e.target.value)}
            placeholder={t.characters.aliases}
            aria-label={t.characters.aliases}
            data-testid="character-aliases"
            className={field}
          />
          <input
            dir="auto"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            placeholder={t.characters.role}
            aria-label={t.characters.role}
            data-testid="character-role"
            className={field}
          />
          <textarea
            dir="auto"
            value={introduction}
            onChange={(e) => setIntroduction(e.target.value)}
            placeholder={t.characters.introduction}
            aria-label={t.characters.introduction}
            data-testid="character-introduction-input"
            rows={2}
            className={field}
          />
          <textarea
            dir="auto"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t.characters.description}
            aria-label={t.characters.description}
            data-testid="character-description"
            rows={4}
            className={field}
          />
          {error && (
            <p
              role="alert"
              data-testid="character-error"
              className="text-sm text-red-600 dark:text-red-400"
            >
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={pending || !name.trim()}
            data-testid="character-save"
            className="w-full rounded-control bg-sage px-4 py-2.5 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-40"
          >
            {pending ? t.characters.saving : t.characters.save}
          </button>

          {onMerge && others.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 border-t border-line pt-3">
              <label className="text-xs text-muted" htmlFor="merge-into">
                {t.characters.mergeInto}
              </label>
              <select
                id="merge-into"
                data-testid="merge-into"
                value={into}
                onChange={(e) => setInto(e.target.value)}
                className="min-w-0 flex-1 rounded-control border border-line bg-surface px-2 py-1.5 text-sm"
              >
                <option value="">{t.characters.mergeChoose}</option>
                {others.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                data-testid="merge-character"
                disabled={pending || !into}
                onClick={() => onMerge(into)}
                className="rounded-control border border-line px-3 py-1.5 text-sm transition-colors hover:bg-sunk disabled:opacity-40"
              >
                {t.characters.merge}
              </button>
            </div>
          )}

          {onDelete && (
            <div className="border-t border-line pt-3">
              <ConfirmDelete
                testId="delete-character"
                label={t.characters.delete}
                question={t.characters.deleteQuestion}
                disabled={pending}
                onConfirm={onDelete}
              />
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
